import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { ALL_EXPERIMENTS } from '@axlabs/experiments';
import styles from './ExperimentList.module.css';

type DomainFilter = 'all' | 'physics' | 'chemistry' | 'biology' | 'mathematics';

export function ExperimentList() {
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter experiments based on domain and search query
  const filteredExperiments = useMemo(() => {
    return ALL_EXPERIMENTS.filter(exp => {
      const matchesDomain = domainFilter === 'all' || exp.domain === domainFilter;
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        searchQuery === '' ||
        exp.name.toLowerCase().includes(searchLower) ||
        exp.description.toLowerCase().includes(searchLower) ||
        exp.conceptTags.some(tag => tag.toLowerCase().includes(searchLower));
      return matchesDomain && matchesSearch;
    });
  }, [domainFilter, searchQuery]);

  // Count experiments by domain
  const experimentCounts = useMemo(() => {
    return {
      all: ALL_EXPERIMENTS.length,
      physics: ALL_EXPERIMENTS.filter(e => e.domain === 'physics').length,
      chemistry: ALL_EXPERIMENTS.filter(e => e.domain === 'chemistry').length,
      biology: ALL_EXPERIMENTS.filter(e => e.domain === 'biology').length,
      mathematics: ALL_EXPERIMENTS.filter(e => e.domain === 'mathematics').length,
    };
  }, []);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>All Experiments</h1>
      <p className={styles.subtitle}>
        Browse and run {experimentCounts.all} interactive experiments across physics, chemistry, biology, and mathematics.
      </p>

      {/* Filter Tabs */}
      <div className={styles.filterBar}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${domainFilter === 'all' ? styles.tabActive : ''}`}
            onClick={() => setDomainFilter('all')}
          >
            All <span className={styles.tabCount}>({experimentCounts.all})</span>
          </button>
          <button
            className={`${styles.tab} ${domainFilter === 'physics' ? styles.tabActive : ''}`}
            onClick={() => setDomainFilter('physics')}
          >
            Physics <span className={styles.tabCount}>({experimentCounts.physics})</span>
          </button>
          <button
            className={`${styles.tab} ${domainFilter === 'chemistry' ? styles.tabActive : ''}`}
            onClick={() => setDomainFilter('chemistry')}
          >
            Chemistry <span className={styles.tabCount}>({experimentCounts.chemistry})</span>
          </button>
          <button
            className={`${styles.tab} ${domainFilter === 'biology' ? styles.tabActive : ''}`}
            onClick={() => setDomainFilter('biology')}
          >
            Biology <span className={styles.tabCount}>({experimentCounts.biology})</span>
          </button>
          <button
            className={`${styles.tab} ${domainFilter === 'mathematics' ? styles.tabActive : ''}`}
            onClick={() => setDomainFilter('mathematics')}
          >
            Mathematics <span className={styles.tabCount}>({experimentCounts.mathematics})</span>
          </button>
        </div>

        {/* Search Input */}
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search experiments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Results Count */}
      <p className={styles.resultsCount}>
        Showing {filteredExperiments.length} of {experimentCounts.all} experiments
      </p>

      {/* Experiment Grid */}
      <div className={styles.grid}>
        {filteredExperiments.map(exp => (
          <Link
            key={exp.id}
            to={`/experiments/${exp.domain}/${exp.slug}`}
            className={styles.card}
            data-domain={exp.domain}
          >
            <div className={styles.cardHeader}>
              <span className={styles.domain}>{exp.domain}</span>
              <span className={styles.difficulty}>
                L{exp.difficulty}
              </span>
            </div>
            <h3 className={styles.name}>{exp.name}</h3>
            <p className={styles.description}>{exp.description}</p>
            <div className={styles.tags}>
              {exp.conceptTags.slice(0, 3).map(tag => (
                <span key={tag} className={styles.tag}>{tag}</span>
              ))}
            </div>
          </Link>
        ))}
      </div>

      {/* No Results Message */}
      {filteredExperiments.length === 0 && (
        <div className={styles.noResults}>
          <p>No experiments found matching your search.</p>
        </div>
      )}
    </div>
  );
}
